/**
 * Thin wrapper around Google Analytics gtag() for custom event tracking.
 * All events are no-ops if gtag is not loaded.
 */

type EventParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Event parameter names GA4 reads as a *traffic source*, not as event data.
 *
 * Send `source: "map"` on an event and GA4 re-credits the whole session to a
 * source called "map". `county_click`, the location events and the
 * jump-to-location button all sent one: in the first ten days of the BigQuery
 * export about 9% of sessions (745) were credited to "landing-hero", "map",
 * "county-map", "finder" and "blog:research:intro" instead of the search
 * engine that actually sent them. Renaming here, at the one place every event
 * passes through, keeps a future call site from doing it again.
 */
const RESERVED_PARAMS = new Set(["source", "medium", "campaign", "term", "content"]);

export function safeEventParams(params?: EventParams): EventParams | undefined {
  if (!params) return params;
  let renamed: EventParams | null = null;
  for (const key of Object.keys(params)) {
    if (!RESERVED_PARAMS.has(key)) continue;
    renamed ??= { ...params };
    renamed[`ui_${key}`] = renamed[key];
    delete renamed[key];
  }
  return renamed ?? params;
}

export function trackEvent(name: string, params?: EventParams) {
  if (typeof window.gtag === "function") {
    window.gtag("event", name, safeEventParams(params));
  }
}
